import React from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import cx from 'classnames/bind';

import styles from './Fade.less';

import { restProps } from '../utils';

const cxb = cx.bind(styles);

/**
 * **Access:**
 * ```js
 * import { Transitions } from '@iss/client-react-components'
 *
 * <Transitions.Fade/>
 * ```
 *
 * This wraps the fade transition in a simple reusable component.
 */
class Child extends React.Component {
  static propTypes = {
    children: PropTypes.any,
    in: PropTypes.bool,
  };

  ref = React.createRef();

  shouldComponentUpdate({ in: fadingIn }) {
    const { current } = this.ref;
    const dom = current && current.getDOMNode ? current.getDOMNode() : current;
    if (dom && !fadingIn) {
      const asPx = (x) => `${x}px`;
      // capture the current position and bake it into the inline-style
      // if (getComputedStyle(dom).boxSizing !== 'border-box') {}
      const { offsetTop: top, offsetLeft: left, offsetHeight: height, offsetWidth: width } = dom;
      Object.assign(dom.style, {
        top: asPx(top),
        bottom: 'auto',
        left: asPx(left),
        right: 'auto',
        width: asPx(width),
        height: asPx(height),
        margin: 0,
      });
    }
    return fadingIn; //fading out needs to stop subtree updates
  }

  render() {
    const { children, className } = this.props;
    const child = React.Children.only(children);
    return React.cloneElement(child, {
      ...restProps(Child, this.props),
      ref: this.ref,
      className: cx(child.props.className, className),
    });
  }
}

export default class Fade extends React.Component {
  static propTypes = {
    appear: PropTypes.bool,
    in: PropTypes.bool,
    direction: PropTypes.oneOf(['up', 'down', 'left', 'left-cont', 'right', 'right-cont']),
  };

  static defaultProps = {
    appear: false,
    direction: 'down',
  };

  render() {
    const {
      props: { appear, onExited, onEntered, in: fadeIn, direction: dir, ...props },
    } = this;

    if (React.Children.only(props.children).type === React.Fragment) {
      throw new TypeError('Expected to receive a single React (non-fragment) element child');
    }

    return (
      <CSSTransition
        in={fadeIn}
        timeout={1000}
        appear={appear}
        enter
        exit
        mountOnEnter
        unmountOnExit
        onEntered={onEntered}
        onExited={onExited}
        classNames={{
          appear: cxb(`appear`, `appear-${dir}`),
          appearActive: cxb(`active-appear-${dir}`),

          enter: cxb(`enter`, `enter-${dir}`),
          enterActive: cxb(`active-enter-${dir}`),
          enterDone: cxb(`enter-done`, `enter-${dir}-done`),

          exit: cxb(`exit`, `exit-${dir}`),
          exitActive: cxb(`active-exit-${dir}`),
          exitDone: cxb(`exit-done`, `exit-${dir}-done`),
        }}
      >
        <Child {...props} in={fadeIn} />
      </CSSTransition>
    );
  }
}
